import { useEffect, useRef, useState } from 'react'
import { provisionJobApi, ProvisionEvent, ProvisionJob } from '../api/client'
import { fmtTimeSec } from '../lib/format'

const ACTIVE = ['queued', 'pending', 'running']

const stateStyle: Record<string, string> = {
  queued: 'bg-[#EEF0FB] text-[#3443A6]',
  pending: 'bg-yellow-100 text-yellow-800',
  running: 'bg-secondary text-primary',
  succeeded: 'bg-[#E2F3E9] text-[#146C43]',
  failed: 'bg-[#FCE4E1] text-[#A3271D]',
  cancelled: 'bg-gray-200 text-gray-800',
}

export function JobState({ state }: { state: string }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${stateStyle[state] || 'bg-muted'}`}>{state}</span>
  )
}

export function ProvisionJobLog({ jobId, title, onClose }: { jobId: number; title: string; onClose: () => void }) {
  const [job, setJob] = useState<ProvisionJob | null>(null)
  const [events, setEvents] = useState<ProvisionEvent[]>([])
  const [error, setError] = useState('')
  const [showConsole, setShowConsole] = useState(true)
  const lastId = useRef(0)
  const bottom = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let stopped = false
    let timer: ReturnType<typeof setTimeout>
    const poll = async () => {
      try {
        const data = await provisionJobApi.get(jobId, lastId.current)
        if (stopped) return
        setJob(data.job)
        if (data.events.length > 0) {
          lastId.current = data.events[data.events.length - 1].id
          setEvents((prev) => [...prev, ...data.events])
        }
        setError('')
        if (!ACTIVE.includes(data.job.state)) return
      } catch (e) {
        if (!stopped) setError(apiError(e))
      }
      if (!stopped) timer = setTimeout(poll, 2000)
    }
    poll()
    return () => {
      stopped = true
      clearTimeout(timer)
    }
  }, [jobId])

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [events.length])

  const cancel = async () => {
    if (!confirm('Cancel this installation? The machine is left in an unknown state.')) return
    try {
      await provisionJobApi.cancel(jobId)
    } catch (e) {
      setError(apiError(e))
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold">Job #{jobId}: {title}</h3>
            {job && (
              <div className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
                <JobState state={job.state} />
                <span>agent {job.agent_id}</span>
                {job.created_by && <span>· started by {job.created_by}</span>}
                {job.cancel_requested && job.state === 'running' && <span>· cancelling…</span>}
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center">
            {events.some((e) => e.kind === 'console') && (
              <label className="text-xs flex items-center gap-1">
                <input type="checkbox" checked={showConsole} onChange={(e) => setShowConsole(e.target.checked)} />
                serial console
              </label>
            )}
            {job && ACTIVE.includes(job.state) && (
              <button onClick={cancel} className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700">
                Cancel
              </button>
            )}
            <button onClick={onClose} className="px-3 py-1 border rounded text-sm hover:bg-muted">
              Close
            </button>
          </div>
        </div>
        {(error || job?.error) && (
          <div className="px-4 py-2 text-sm text-[#A3271D] bg-[#FCE4E1] border-b">{error || job?.error}</div>
        )}
        <div className="flex-1 overflow-auto bg-black text-green-200 font-mono text-xs p-3">
          {events.length === 0 ? (
            <div className="text-gray-400">
              {job?.state === 'queued' ? 'In the queue: it starts by itself when what is before it is done.' : job?.state === 'pending' ? 'Waiting for the agent to take the job (next heartbeat).' : 'No log yet.'}
            </div>
          ) : (
            events.filter((e) => showConsole || e.kind !== 'console').map((e) => (
              <div key={e.id} className="whitespace-pre-wrap">
                <span className="text-gray-400">{fmtTimeSec(e.time).slice(11)}</span>{' '}
                <span className={kindColor(e.kind)}>{e.kind.padEnd(7)}</span>{' '}
                <span className="text-gray-400">{(e.mac || '-').padEnd(17)}</span> {e.detail}
              </div>
            ))
          )}
          <div ref={bottom} />
        </div>
      </div>
    </div>
  )
}

function kindColor(kind: string) {
  switch (kind) {
    case 'error':
      return 'text-red-400'
    case 'status':
      return 'text-yellow-300'
    case 'ignored':
      return 'text-gray-500'
    case 'console':
      return 'text-gray-200'
    default:
      return 'text-cyan-300'
  }
}

export function apiError(e: unknown): string {
  const err = e as { response?: { data?: { error?: string } }; message?: string }
  return err?.response?.data?.error || err?.message || 'request failed'
}
